<template>
  <div class="gw-completed-list">
    <RefreshOnscrollList class="f-h" @scrollBottom="refresh(true)">
      <GwCompletedItem
        v-for="gw in gwList"
        ref="gwItems"
        :key="gw.id"
        :gw="gw"
        @delete="deleteGW(gw)"
      />
    </RefreshOnscrollList>
    <LoadingOverlay v-model="gwLoading" />
  </div>
</template>

<script>
/**
 * Completed gemba walks list widget
 */

import Vue from 'vue';
import toastr from 'toastr';
// Vuex helpers
import { createNamespacedHelpers } from 'vuex';
import { FETCH_GW_TEMPLATES } from '@/store/modules/gemba-walks/action-types';
import { GET_GW_TEMPLATES } from '@/store/modules/gemba-walks/mutation-types';
// Import components
import mixinWidgets from '@/mixins/widgets';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import mixinCompletedGwOptions from '@/mixins/page-options/gemba-walks.vue';
import GwCompletedItem from '@/components/gw-completed-item.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
import RefreshOnscrollList from '@/components/refresh-onscroll-list.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';

const { mapState, mapActions } = createNamespacedHelpers('gembaWalks');
const GW_REQ_LIMIT = 30;


export default {

  name: 'GwCompletedListWidget',

  components: {
    GwCompletedItem,
    LoadingOverlay,
    RefreshOnscrollList,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinSocketHandler, mixinCompletedGwOptions],

  props: {
    sortBy: {
      type: Object,
      default: () => ({ value: 0, desc: true }),
      validator: v => v.value === 0,
    },
    templates: {
      type: Array,
      default: () => [],
      validator: v => v.every(t => t > 0),
    },
    which: {
      type: Number,
      default: 0,
      validator: v => v === 0 || v === 1,
    },
  },

  data() {
    return {
      gwListData: [],
      sqlLimit: GW_REQ_LIMIT,
      itemsLimitOffset: 0,
      refreshTimeout: null,
      gwLoading: false,
    };
  },

  computed: {
    config() {
      const specOptions = this.cfgTemplateEdition
        ? this.completedGwOptions.filter(opt => !['templates', 'templatesTitle'].includes(opt.key))
        : this.completedGwOptions;
      const config = [
        ...specOptions,
        this.defaultBgColorConfig,
      ];
      return config;
    },
    templateItems() {
      if (!this.templatesData) {
        return [];
      }
      return this.templatesData.map(template => ({
        id: Number(template.id),
        label: template.name || this.$t('message.GW_NO_NAME'),
      }));
    },
    templatesDisabled() {
      return this.which === 1;
    },
    gwList() {
      let { gwListData } = this;
      if (!this.templatesDisabled) {
        gwListData = gwListData.filter(gw => this.completedGwOptSelectedTemplates.includes(Number(gw.template)));
      }
      switch (this.sortBy.value) {
        case 0: return !this.sortBy.desc
          ? gwListData.sort((a, b) => a.mDate.valueOf() - b.mDate.valueOf())
          : gwListData.sort((a, b) => b.mDate.valueOf() - a.mDate.valueOf());

        default: return gwListData;
      }
    },
    fetchParams() {
      return {
        completed: true,
        audited: this.completedGwOptWhich === 1,
        templates: this.completedGwOptSelectedTemplates,
        sortBy: { index: this.completedGwOptSortBy.value, desc: this.completedGwOptSortBy.desc },
        limit: { offset: this.itemsLimitOffset, count: this.sqlLimit },

      };
    },
    completedGwOptSelectedTemplates() {
      return this.templates && this.templates.length ? this.templates : this.completedGwOptDefaultTemplates;
    },
    completedGwOptSortBy() {
      return this.sortBy;
    },
    completedGwOptWhich() {
      return this.which;
    },
    ...mapState({
      templatesData: state => state[GET_GW_TEMPLATES.stateKey],
      templatesLoading: state => state[GET_GW_TEMPLATES.loadingKey],
    }),
  },

  watch: {
    completedGwOptWhich() {
      this.refresh();
    },
    completedGwOptSelectedTemplates() {
      this.refresh();
    },
    completedGwOptSortBy() {
      this.refresh();
    },
  },

  created() {
    if (this.editable && !this.templatesData && !this.templatesLoading && !this.cfgTemplateEdition) {
      this.FETCH_GW_TEMPLATES();
    }
    this.refresh();
    this.handleSocketMessage(SocketMessages.CC_MSGS.GW_END, () => {
      this.refresh();
    });
  },

  beforeDestroy() {
    clearTimeout(this.refreshTimeout);
  },

  methods: {
    refresh(increaseLimit) {
      if (this.cfgTemplateEdition) {
        return;
      }
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = setTimeout(() => {
        this.fetchGWs(increaseLimit);
      }, 150);
    },
    fetchGWs(increaseLimit) {
      if (increaseLimit !== true) {
        this.sqlLimit = GW_REQ_LIMIT;
        this.itemsLimitOffset = 0;
      }
      this.gwLoading = true;
      Vue.axios({
        method: 'post',
        url: '/server/gembaWalks/get.php',
        data: this.fetchParams,
      })
        .then(({ data }) => {
          if (Array.isArray(data)) {
            if (increaseLimit !== true) {
              this.gwListData = [];
            }
            this.itemsLimitOffset += data.length;
            this.gwListData.push(...data.map(gw => ({ ...gw, mDate: this.$moment(gw.date) })));
          }
          this.gwLoading = false;
        })
        .catch((err) => {
          this.gwLoading = false;
        });
    },
    deleteGW(gw) {
      this.App.post('/server/gembaWalks/delete.php', { id: gw.id }, (data) => {
        if (data == 1) {
          const index = this.gwListData.indexOf(gw);
          if (index !== -1) {
            this.itemsLimitOffset--;
            this.gwListData.splice(index, 1);
          }
          toastr.success(this.$t('message.GW_ITEM_DELETED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    sizeChanged() {
      if (this.$refs.gwItems) {
        this.$refs.gwItems.forEach((comp) => {
          if (comp && comp.onSizeChanged) {
            comp.onSizeChanged();
          }
        });
      }
    },
    ...mapActions({
      FETCH_GW_TEMPLATES,
    }),
  },
};
</script>
